import { useNavigate, useParams } from 'react-router-dom';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { BaseSyntheticEvent, useEffect, useState } from 'react';
import axios from 'axios';
import { Button, Card, Checkbox, FormControlLabel, IconButton, InputAdornment, Stack, TextField, Tooltip } from '@mui/material';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { AuthHeader } from '../../../utils/helpers/auth-headers';
import { setAlertData } from '../../../stores/alertStore';
import { AlertSeverity } from '../../../utils/enum/Alerts';
import { PexipInstance } from '../../../models/PexipInstance';

function InstancesEdit() {
  const { t } = useTranslation();

  const { instanceId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const initError = {
    nameError: false,
    nameHelper: '',
    pexipServerUrlError: false,
    pexipServerUrlHelper: '',
    pexipServerUserError: false,
    pexipServerUserHelper: '',
    pexipServerPasswordError: false,
    pexipServerPasswordHelper: '',
    virtuvisioServerUrlError: false,
    virtuvisioServerUrlHelper: '',
    virtuvisioServerUserError: false,
    virtuvisioServerUserHelper: '',
    virtuvisioServerPasswordError: false,
    virtuvisioServerPasswordHelper: '',
  };
  const [hasError, setHasError] = useState(initError);
  const [instance, setInstance] = useState<PexipInstance>({
    index: 0,
    isVirtuvisio: false,
    name: '',
    pexipServerPassword: '',
    pexipServerUrl: '',
    pexipServerUser: '',
    virtuvisioServerPassword: '',
    virtuvisioServerUrl: '',
    virtuvisioServerUser: '',
    _id: ''
  });

  const [checked, setChecked] = useState(false);

  const [showPexipPassword, setShowPexipPassword] = useState(false);
  const handleClickShowPexipPassword = () => setShowPexipPassword(!showPexipPassword);
  const handleMouseDownPexipPassword = () => setShowPexipPassword(!showPexipPassword);

  const [showVirtuvisioPassword, setShowVirtuvisioPassword] = useState(false);
  const handleClickShowVirtuvisioPassword = () => setShowVirtuvisioPassword(!showVirtuvisioPassword);
  const handleMouseDownVirtuvisioPassword = () => setShowVirtuvisioPassword(!showVirtuvisioPassword);

  useEffect(() => {
    axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance/${instanceId}`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => {
        setInstance(response.data.data);
        setChecked(response.data.data.isVirtuvisio);
      })
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
          navigate('/configuration/users');
        }
        console.error(error);
      });
  }, [instanceId, navigate]);

  const handleChange = (event: any) => {
    setInstance({ ...instance, [event.target.name]: event.target.value });
  };

  const handleChangeCheckbox = (event: any) => {
    setInstance({ ...instance, [event.target.name]: event.target.checked });
    setChecked(event.target.checked);
  };

  const editInstance = () => {
    axios.patch(`${window._env_.REACT_APP_API_URI}/pexipInstance/${instanceId}`, instance, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then(() => {
        navigate('/configuration/instances');
      })
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
          dispatch(setAlertData({
            message: err.data.message,
            severity: AlertSeverity.ERROR,
            open: true,
          }));
        }
        console.error(error);
      });
  };

  const onEditInstance = (event: BaseSyntheticEvent) => {
    event.preventDefault();
    const tempError = { ...initError };
    if (instance.name === '') {
      tempError.nameError = true;
      tempError.nameHelper = 'InstancesCreate.nameRequired';
    }
    if (instance.pexipServerUrl === '') {
      tempError.pexipServerUrlError = true;
      tempError.pexipServerUrlHelper = 'InstancesCreate.pexipServerUrlRequired';
    }
    if (instance.pexipServerUser === '') {
      tempError.pexipServerUserError = true;
      tempError.pexipServerUserHelper = 'InstancesCreate.pexipServerUserRequired';
    }
    if (instance.pexipServerPassword === '') {
      tempError.pexipServerPasswordError = true;
      tempError.pexipServerPasswordHelper = 'InstancesCreate.pexipServerPasswordRequired';
    }
    if (instance.pexipServerPassword.length < 6) {
      tempError.pexipServerPasswordError = true;
      tempError.pexipServerPasswordHelper = 'InstancesCreate.pexipServerPasswordTooShort';
    }
    if (instance.isVirtuvisio) {
      if (instance.virtuvisioServerUrl === '') {
        tempError.virtuvisioServerUrlError = true;
        tempError.virtuvisioServerUrlHelper = 'InstancesCreate.virtuvisioServerUrlRequired';
      }
      if (instance.virtuvisioServerUser === '') {
        tempError.virtuvisioServerUserError = true;
        tempError.virtuvisioServerUserHelper = 'InstancesCreate.virtuvisioServerUserRequired';
      }
      if (instance.virtuvisioServerPassword === '') {
        tempError.virtuvisioServerPasswordError = true;
        tempError.virtuvisioServerPasswordHelper = 'InstancesCreate.virtuvisioServerPasswordRequired';
      }
      if (instance.virtuvisioServerPassword.length < 6) {
        tempError.virtuvisioServerPasswordError = true;
        tempError.virtuvisioServerPasswordHelper = 'InstancesCreate.virtuvisioServerPasswordTooShort';
      }
    }
    setHasError(tempError);
    if (_.isEqual(tempError, initError)) {
      editInstance();
    }
  };

  const cancelEdit = () => {
    navigate('/configuration/instances');
  };

  return (
    <div className="category-container">
      <Card>
        <div className="card-title">{ t('InstancesEdit.title') }</div>
        <div>
          <form onSubmit={onEditInstance} className="form">
            <TextField
              variant="outlined"
              label={t('InstancesEdit.name')}
              error={hasError.nameError}
              helperText={hasError.nameError ? t(hasError.nameHelper) : ''}
              value={instance.name}
              onChange={handleChange}
              type="text"
              name="name"
              required
            />
            <TextField
              variant="outlined"
              label={t('InstancesEdit.pexipServerUrl')}
              error={hasError.pexipServerUrlError}
              helperText={hasError.pexipServerUrlError ? t(hasError.pexipServerUrlHelper) : ''}
              value={instance.pexipServerUrl}
              onChange={handleChange}
              type="text"
              name="pexipServerUrl"
              required
            />
            <TextField
              variant="outlined"
              label={t('InstancesEdit.pexipServerUser')}
              error={hasError.pexipServerUserError}
              helperText={hasError.pexipServerUserError ? t(hasError.pexipServerUserHelper) : ''}
              value={instance.pexipServerUser}
              onChange={handleChange}
              type="text"
              name="pexipServerUser"
              required
            />
            <TextField
              variant="outlined"
              label={t('InstancesEdit.pexipServerPassword')}
              error={hasError.pexipServerPasswordError}
              helperText={hasError.pexipServerPasswordError ? t(hasError.pexipServerPasswordHelper) : ''}
              value={instance.pexipServerPassword}
              onChange={handleChange}
              type={showPexipPassword ? 'text' : 'password'}
              name="pexipServerPassword"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPexipPassword}
                      onMouseDown={handleMouseDownPexipPassword}
                    >
                      {showPexipPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={checked}
                  value={instance.isVirtuvisio}
                  onChange={handleChangeCheckbox}
                  name="isVirtuvisio"
                />
              )}
              label={t('InstancesEdit.isVirtuvisio') as string}
            />
            {instance.isVirtuvisio && (
              <>
                <TextField
                  variant="outlined"
                  label={t('InstancesEdit.virtuvisioServerUrl')}
                  error={hasError.virtuvisioServerUrlError}
                  helperText={hasError.virtuvisioServerUrlError ? t(hasError.virtuvisioServerUrlHelper) : ''}
                  value={instance.virtuvisioServerUrl}
                  onChange={handleChange}
                  type="text"
                  name="virtuvisioServerUrl"
                />
                <TextField
                  variant="outlined"
                  label={t('InstancesEdit.virtuvisioServerUser')}
                  error={hasError.virtuvisioServerUserError}
                  helperText={hasError.virtuvisioServerUserError ? t(hasError.virtuvisioServerUserHelper) : ''}
                  value={instance.virtuvisioServerUser}
                  onChange={handleChange}
                  type="text"
                  name="virtuvisioServerUser"
                />
                <TextField
                  variant="outlined"
                  label={t('InstancesEdit.virtuvisioServerPassword')}
                  error={hasError.virtuvisioServerPasswordError}
                  helperText={hasError.virtuvisioServerPasswordError ? t(hasError.virtuvisioServerPasswordHelper) : ''}
                  value={instance.virtuvisioServerPassword}
                  onChange={handleChange}
                  type={showVirtuvisioPassword ? 'text' : 'password'}
                  name="virtuvisioServerPassword"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowVirtuvisioPassword}
                          onMouseDown={handleMouseDownVirtuvisioPassword}
                        >
                          {showVirtuvisioPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </>
            )}
            <Stack direction="row" spacing={2} alignSelf="center" width="100%">
              <Button fullWidth onClick={cancelEdit} variant="outlined">{ t('InstancesEdit.button.cancel') }</Button>
              <Button fullWidth type="submit" value="submit" variant="contained">{ t('InstancesEdit.button.submit') }</Button>
            </Stack>
          </form>
          <div>
            <h4>{ t('InstancesEdit.eventSinkLink') }</h4>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <span>{`${window._env_.REACT_APP_EVENT_SINK_URI}/eventSink/${instance._id}`}</span>
              <Tooltip title={t('InstancesList.button.copyEventSinkLink') as string} arrow>
                <Button color="primary"><FontAwesomeIcon icon={faCopy} /></Button>
              </Tooltip>
            </div>

          </div>
        </div>
      </Card>
    </div>
  );
}

export default InstancesEdit;
